import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex } from '@/UI/styles';
import NodeDataCard from '@/components/Common/NodeDataCard/NodeDataCard';
import { I18N_KEYS } from '@/language/keys';
import { useWorkflowStore } from '@/state/workflow.state';
import { useUserStore } from '@/state/user.state';
import { useMemberName } from '@/queries/useWorkspace';
import { getModelPriceInfo } from '@/components/Nodes/Utils';
import type { ModelItem } from '@/state/nodes/nodes.types';
import type { SubflowTemplateResponseDto } from '@/types/api/subflowTemplate';
import type { AiModelData } from '@/types/nodes/model';

export const NodeDetails = ({
  item,
  shouldDisplayEmptyOutput,
  template,
}: {
  item: ModelItem;
  shouldDisplayEmptyOutput?: boolean;
  template?: SubflowTemplateResponseDto;
}) => {
  const { t } = useTranslation();
  const nodeTypes = useWorkflowStore((state) => state.nodeTypes);
  const modelPricesMaps = useWorkflowStore((state) => state.modelPricesMaps);
  const workspaceFalByokEnabled = useWorkflowStore((state) => state.workspaceFalByokEnabled);
  const userId = useUserStore((state) => state.user?.id);
  const memberName = useMemberName(template?.createdBy);

  const importedModelName = useMemo<string | undefined>(() => {
    const node = template?.data?.nodes?.[0];
    return node ? (node.data as AiModelData)?.model?.name : undefined;
  }, [template?.data?.nodes]);

  // TODO(backend): once the menu endpoint populates `price` on saved items, this lookup
  // becomes unnecessary — item.price will already have the correct value and the early
  const templatePrice = useMemo(() => {
    if (item.price != null || !template?.data?.nodes?.[0]) {
      return undefined;
    }
    const nodeData = template.data.nodes[0].data as AiModelData;
    if (!nodeData?.model) {
      return undefined;
    }
    return getModelPriceInfo(nodeData.model, modelPricesMaps, nodeData.params, { workspaceFalByokEnabled });
  }, [item.price, template?.data?.nodes, modelPricesMaps, workspaceFalByokEnabled]);

  const sourceNodeDef = useMemo(
    () =>
      template?.sourceNodeDefinitionId
        ? nodeTypes.find((nd) => nd.type === template.sourceNodeDefinitionId)
        : undefined,
    [nodeTypes, template?.sourceNodeDefinitionId],
  );

  // Saved items don't carry verification metadata or a polished display name on the menu DTO.
  // Look up the catalog node-definition by model name and reuse its metadata. Mirrors ModelDetails.tsx.
  const sourceModelDef = useMemo(() => {
    if (!importedModelName) {
      return undefined;
    }
    return nodeTypes.find((nd) => nd.data?.model?.name === importedModelName);
  }, [nodeTypes, importedModelName]);

  const sourceNodeName = useMemo(() => {
    const menuDisplayName = (sourceModelDef?.data as { menu?: { displayName?: string } } | undefined)?.menu
      ?.displayName;
    const name = menuDisplayName ?? importedModelName ?? sourceNodeDef?.data?.name;
    if (!name) {
      return undefined;
    }
    return t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.NODE_DETAILS.BASED_ON, { name });
  }, [sourceModelDef, importedModelName, sourceNodeDef, t]);

  const createdByName = useMemo(() => {
    if (!template?.createdBy) {
      return undefined;
    }
    if (template.createdBy === userId) {
      return t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.NODE_DETAILS.CREATED_BY_ME);
    }
    if (!memberName) {
      return undefined;
    }
    return t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.NODE_DETAILS.CREATED_BY, { name: memberName });
  }, [template?.createdBy, userId, memberName, t]);

  return (
    <Flex sx={{ width: 320 }}>
      <NodeDataCard
        displayName={item.displayName}
        description={item.description ?? ''}
        price={item.price ?? templatePrice?.credits}
        isByok={item.isByok === true || templatePrice?.isByok === true}
        isVerified={sourceModelDef?.metadata?.isVerified ?? item.isVerified}
        termsUrls={sourceModelDef?.metadata?.termsUrls ?? item.termsUrls ?? []}
        privacyUrl={sourceModelDef?.metadata?.privacyPolicyUrl ?? item.privacyPolicyUrl ?? ''}
        inputTypes={
          item.inputTypes?.length ? item.inputTypes : (sourceModelDef?.metadata?.inputTypes ?? item.inputTypes)
        }
        outputTypes={
          item.outputTypes?.length ? item.outputTypes : (sourceModelDef?.metadata?.outputTypes ?? item.outputTypes)
        }
        shouldDisplayEmptyOutput={shouldDisplayEmptyOutput}
        sourceNodeName={sourceNodeName}
        createdByName={createdByName}
      />
    </Flex>
  );
};
